// ProposalTemplates.tsx — admin page: manage proposal categories and templates.
//
// Route (sidebar entry is hidden for staff in routeConfig.ts):
//   <Route path="/proposals" element={<ProposalTemplates />} />
//
// ROLES
//   admin → everything on this page
//   staff → never sees the menu entry; opening the URL shows an "admins only"
//           panel and NOTHING is fetched (the API would refuse it anyway)
//   role not loaded yet → spinner, nothing fetched, nothing admin-only shown

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  FileText,
  Folder,
  FolderPlus,
  Layers,
  Lock,
  Mail,
  MessageCircle,
  Paperclip,
  Pencil,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import { G, C } from '../../components/GlobalComponents/uiTokens';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import CategoryFormModal from './parts/CategoryFormModal';
import TemplateFormModal from './parts/TemplateFormModal';
import ProposalDeleteModal from './parts/ProposalDeleteModal';
import { Notice, SHELL, INNER } from './parts/ProposalDialog';
import ProposalsListSkeleton from '../../components/skeletons/ProposalsListSkeleton';
import {
  FALLBACK_AUTO_VARIABLES,
  ProposalCategory,
  ProposalChannel,
  ProposalTemplate,
  apiError,
  fmtDate,
  fmtSize,
  proposalApi,
  useProposalRole,
} from './parts/proposalTypes';

type ChannelFilter = 'all' | ProposalChannel;

const CHANNEL_META: Record<ProposalChannel, { label: string; icon: typeof Mail; color: string }> = {
  email: { label: 'Email', icon: Mail, color: C.teal },
  whatsapp: { label: 'WhatsApp', icon: MessageCircle, color: '#128c4a' },
};

const TH: React.CSSProperties = {
  textAlign: 'left',
  padding: '9px 12px',
  fontSize: '0.6rem',
  fontWeight: 700,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  color: C.sub,
  background: G.shellBg,
  borderBottom: `1px solid ${G.line}`,
  whiteSpace: 'nowrap',
};
const TD: React.CSSProperties = { padding: '10px 12px', fontSize: '0.75rem', color: '#1f2937', borderBottom: `1px solid ${G.softer}`, verticalAlign: 'middle' };

const iconBtn = (danger = false): React.CSSProperties => ({
  width: '28px',
  height: '28px',
  borderRadius: '7px',
  border: `1px solid ${danger ? 'rgba(239,68,68,0.3)' : G.line}`,
  background: danger ? 'rgba(239,68,68,0.06)' : C.white,
  color: danger ? '#c62828' : C.teal,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const MiniSwitch: React.FC<{ on: boolean; busy: boolean; onClick: () => void; label: string }> = ({ on, busy, onClick, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={busy}
    onClick={onClick}
    title={on ? 'Active — click to hide it from the send form' : 'Inactive — click to make it available'}
    style={{ width: '34px', height: '20px', borderRadius: '20px', border: 'none', padding: '2px', background: on ? C.teal : '#d1d5db', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1, transition: 'background 0.2s', display: 'flex', alignItems: 'center', justifyContent: on ? 'flex-end' : 'flex-start' }}
  >
    <span style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.25)' }} />
  </button>
);

const ProposalTemplates: React.FC = () => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const { isAdmin, roleKnown, profileError } = useProposalRole();

  const [categories, setCategories] = useState<ProposalCategory[]>([]);
  const [templates, setTemplates] = useState<ProposalTemplate[]>([]);
  const [autoVariables, setAutoVariables] = useState<string[]>(FALLBACK_AUTO_VARIABLES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // filters
  const [categoryFilter, setCategoryFilter] = useState<string>('all'); // 'all' or a category id
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>('all');
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  // modals
  const [categoryModal, setCategoryModal] = useState<{ open: boolean; item: ProposalCategory | null }>({ open: false, item: null });
  const [templateModal, setTemplateModal] = useState<{ open: boolean; item: ProposalTemplate | null }>({ open: false, item: null });
  const [deleteTarget, setDeleteTarget] = useState<{ kind: 'category' | 'template'; id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());

  const setBusy = (id: string, on: boolean) =>
    setBusyIds((s) => {
      const n = new Set(s);
      if (on) n.add(id);
      else n.delete(id);
      return n;
    });

  // ── Load (admin only) ──────────────────────────────────────────────────────
  const load = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [cats, tpls] = await Promise.all([proposalApi.categories.list(), proposalApi.templates.list()]);
      setCategories(cats.data ?? []);
      setTemplates(tpls.data ?? []);
      if (tpls.auto_variables?.length) setAutoVariables(tpls.auto_variables);
      setError(null);
    } catch (e) {
      if (!silent) setError(apiError(e, 'Failed to load proposal templates'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  // a selected category that no longer exists falls back to "all"
  useEffect(() => {
    if (categoryFilter !== 'all' && !categories.some((c) => c.id === categoryFilter)) setCategoryFilter('all');
  }, [categories, categoryFilter]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return templates.filter((t) => {
      if (categoryFilter !== 'all' && t.category_id !== categoryFilter) return false;
      if (channelFilter !== 'all' && t.channel !== channelFilter) return false;
      if (q && !`${t.name} ${t.subject}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [templates, categoryFilter, channelFilter, query]);

  const activeCategory = categories.find((c) => c.id === categoryFilter) ?? null;

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleToggleActive = async (t: ProposalTemplate) => {
    setBusy(t.id, true);
    try {
      const form = new FormData();
      form.append('is_active', String(!t.is_active));
      const res = await proposalApi.templates.update(t.id, form);
      setTemplates((list) => list.map((x) => (x.id === t.id ? res.data : x)));
      toast.current?.show({ severity: 'success', summary: res.data.is_active ? 'Template active' : 'Template hidden', detail: t.name, life: 2200 });
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Update failed', detail: apiError(e, 'Try again'), life: 3500 });
    } finally {
      setBusy(t.id, false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      if (deleteTarget.kind === 'template') await proposalApi.templates.remove(deleteTarget.id);
      else await proposalApi.categories.remove(deleteTarget.id);
      toast.current?.show({ severity: 'success', summary: deleteTarget.kind === 'template' ? 'Template deleted' : 'Category deleted', detail: deleteTarget.name, life: 2500 });
      setDeleteTarget(null);
      load(true);
    } catch (e) {
      setDeleteError(apiError(e, 'Could not delete it. Try again.')); // e.g. 409: category still has templates
    } finally {
      setDeleting(false);
    }
  };

  // ── Guards ─────────────────────────────────────────────────────────────────
  if (!roleKnown) {
    return (
      <div style={SHELL}>
        <div style={{ ...INNER, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '240px', padding: '20px' }}>
          {profileError ? (
            <Message severity="error" text={`Could not check your role: ${profileError}`} style={{ fontSize: '0.83rem' }} />
          ) : (
            <ProgressSpinner style={{ width: '34px', height: '34px' }} strokeWidth="3" animationDuration=".8s" />
          )}
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={SHELL}>
        <div style={{ ...INNER, padding: '34px 22px', textAlign: 'center' }}>
          <div style={{ width: 46, height: 46, margin: '0 auto 12px', borderRadius: '12px', background: C.limeDim, border: `1px solid ${C.limeBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Lock size={20} color={C.teal} />
          </div>
          <div style={{ fontSize: '0.95rem', fontWeight: 700, color: C.teal, marginBottom: '5px' }}>Only admins can manage proposal templates</div>
          <div style={{ fontSize: '0.78rem', color: C.sub, marginBottom: '16px' }}>You can still send a proposal from any lead — open the lead and use the Proposals card.</div>
          <GlobalButton label="BACK TO LEADS" icon={ArrowLeft} onClick={() => navigate('/leads')} />
        </div>
      </div>
    );
  }

  // ── Admin view ─────────────────────────────────────────────────────────────
  return (
    <div style={{ margin: '0 auto' }}>
      <style>{`
        .pt-grid { display: grid; grid-template-columns: 250px minmax(0, 1fr); gap: 11px; align-items: start; }
        @media (max-width: 960px) { .pt-grid { grid-template-columns: 1fr; } }
        .pt-cat:hover { background: ${G.shellBg}; }
        .pt-row:hover td { background: #fbfcfc; }
      `}</style>
      <Toast ref={toast} />

      <CategoryFormModal
        visible={categoryModal.open}
        category={categoryModal.item}
        onHide={() => setCategoryModal({ open: false, item: null })}
        onSaved={(c) => {
          toast.current?.show({ severity: 'success', summary: categoryModal.item ? 'Category updated' : 'Category created', detail: c.name, life: 2500 });
          load(true); // counts + the category name shown on each template
        }}
      />

      <TemplateFormModal
        visible={templateModal.open}
        template={templateModal.item}
        categories={categories}
        defaultCategoryId={categoryFilter !== 'all' ? categoryFilter : null}
        autoVariables={autoVariables}
        onHide={() => setTemplateModal({ open: false, item: null })}
        onSaved={(t) => {
          toast.current?.show({ severity: 'success', summary: templateModal.item ? 'Template updated' : 'Template created', detail: t.name, life: 2500 });
          load(true);
        }}
      />

      <ProposalDeleteModal
        visible={!!deleteTarget}
        kind={deleteTarget?.kind ?? 'template'}
        name={deleteTarget?.name}
        loading={deleting}
        error={deleteError}
        onCancel={() => {
          if (!deleting) {
            setDeleteTarget(null);
            setDeleteError(null);
          }
        }}
        onConfirm={confirmDelete}
      />

      {/* header */}
      <div style={{ ...SHELL, marginBottom: '11px' }}>
        <div style={{ ...INNER, padding: '13.8px 16.6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '11px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '11px', minWidth: 0 }}>
            <div style={{ width: 38, height: 38, borderRadius: '10px', background: `linear-gradient(135deg, ${C.teal}, ${C.tealMid})`, color: C.lime, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <FileText size={18} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h1 style={{ margin: 0, fontSize: '1.01rem', fontWeight: 700, color: C.teal }}>Proposals</h1>
              <div style={{ fontSize: '0.71rem', color: C.sub }}>
                {templates.length} template{templates.length === 1 ? '' : 's'} in {categories.length} categor{categories.length === 1 ? 'y' : 'ies'} · your team sends these from a lead&apos;s page
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '7.4px', flexWrap: 'wrap' }}>
            <GlobalButton label="NEW CATEGORY" variant="outline" size="small" icon={FolderPlus} onClick={() => setCategoryModal({ open: true, item: null })} />
            <GlobalButton label="NEW TEMPLATE" size="small" icon={Plus} onClick={() => setTemplateModal({ open: true, item: null })} />
          </div>
        </div>
      </div>

      {loading ? (
        <ProposalsListSkeleton />
      ) : error ? (
        <div style={SHELL}>
          <div style={{ ...INNER, padding: '20px' }}>
            <Message severity="error" text={error} style={{ marginBottom: '14px', width: '100%', fontSize: '0.83rem' }} />
            <GlobalButton label="TRY AGAIN" onClick={() => load()} />
          </div>
        </div>
      ) : (
        <div className="pt-grid">
          {/* ── Categories ─────────────────────────────────────────────── */}
          <div style={SHELL}>
            <div style={{ ...INNER, padding: '10px' }}>
              <div style={{ padding: '4px 6px 8px', fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.6px', textTransform: 'uppercase', color: C.muted }}>Categories</div>

              <button
                type="button"
                className="pt-cat"
                onClick={() => setCategoryFilter('all')}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '7.4px 8px', borderRadius: '7.4px', border: 'none', background: categoryFilter === 'all' ? C.limeDim : 'transparent', cursor: 'pointer', textAlign: 'left' }}
              >
                <Layers size={13} color={C.teal} />
                <span style={{ flex: 1, fontSize: '0.76rem', fontWeight: categoryFilter === 'all' ? 800 : 600, color: C.teal }}>All templates</span>
                <span style={{ fontSize: '0.64rem', fontWeight: 700, color: C.sub }}>{templates.length}</span>
              </button>

              {categories.map((c) => {
                const on = categoryFilter === c.id;
                return (
                  <div key={c.id} className="pt-cat" style={{ display: 'flex', alignItems: 'center', gap: '4px', borderRadius: '7.4px', background: on ? C.limeDim : 'transparent', marginTop: '2px' }}>
                    <button
                      type="button"
                      onClick={() => setCategoryFilter(c.id)}
                      title={c.description || c.name}
                      style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px', padding: '7.4px 8px', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
                    >
                      <Folder size={13} color={C.tealMid} style={{ flexShrink: 0 }} />
                      <span style={{ flex: 1, minWidth: 0, fontSize: '0.76rem', fontWeight: on ? 800 : 600, color: C.teal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                      <span style={{ fontSize: '0.64rem', fontWeight: 700, color: C.sub }}>{c.template_count}</span>
                    </button>
                    <button type="button" title="Edit category" onClick={() => setCategoryModal({ open: true, item: c })} style={{ ...iconBtn(), width: '24px', height: '24px', border: 'none', background: 'transparent' }}>
                      <Pencil size={11} />
                    </button>
                    <button
                      type="button"
                      title="Delete category"
                      onClick={() => {
                        setDeleteError(null);
                        setDeleteTarget({ kind: 'category', id: c.id, name: c.name });
                      }}
                      style={{ ...iconBtn(true), width: '24px', height: '24px', border: 'none', background: 'transparent', marginRight: '3px' }}
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                );
              })}

              {categories.length === 0 && (
                <div style={{ margin: '8px 4px 4px', padding: '14px 10px', textAlign: 'center', fontSize: '0.72rem', color: C.muted, background: G.shellBg, border: `1px dashed ${G.line}`, borderRadius: '8.3px' }}>
                  No categories yet.
                  <div style={{ marginTop: '8px' }}>
                    <GlobalButton label="CREATE ONE" size="small" icon={FolderPlus} onClick={() => setCategoryModal({ open: true, item: null })} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Templates ──────────────────────────────────────────────── */}
          <div style={SHELL}>
            <div style={{ ...INNER, overflow: 'hidden' }}>
              <div style={{ padding: '11px 13px', display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap', borderBottom: `1px solid ${G.line}` }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 700, color: C.teal, marginRight: 'auto' }}>
                  {activeCategory ? activeCategory.name : 'All templates'}
                  <span style={{ marginLeft: '7px', padding: '1.8px 7px', borderRadius: '20px', background: 'rgba(17,85,80,0.07)', color: C.tealMid, fontSize: '0.64rem', fontWeight: 600 }}>{visible.length}</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '34px', padding: '0 10px', border: `1px solid ${G.line}`, borderRadius: '8px', background: C.white, minWidth: '180px' }}>
                  <Search size={13} color={C.muted} />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search templates…"
                    style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontSize: '0.75rem', fontFamily: 'inherit' }}
                  />
                </div>

                {(['all', 'email', 'whatsapp'] as ChannelFilter[]).map((ch) => {
                  const on = channelFilter === ch;
                  return (
                    <button
                      key={ch}
                      type="button"
                      onClick={() => setChannelFilter(ch)}
                      style={{ padding: '0 11px', height: '34px', borderRadius: '8px', border: `1px solid ${on ? C.limeBorder : G.line}`, background: on ? C.limeDim : C.white, color: on ? '#4a5e00' : C.sub, fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer' }}
                    >
                      {ch === 'all' ? 'All' : CHANNEL_META[ch].label}
                    </button>
                  );
                })}
              </div>

              {templates.length === 0 ? (
                <div style={{ padding: '34px 18px', textAlign: 'center' }}>
                  <FileText size={26} color={C.muted} style={{ opacity: 0.5, marginBottom: '8px' }} />
                  <div style={{ fontSize: '0.84rem', fontWeight: 700, color: C.teal, marginBottom: '4px' }}>No templates yet</div>
                  <div style={{ fontSize: '0.74rem', color: C.sub, marginBottom: '14px' }}>
                    {categories.length === 0 ? 'Start with a category, then add your first template to it.' : 'Write your first template — your team can then send it from any lead.'}
                  </div>
                  <GlobalButton
                    label={categories.length === 0 ? 'CREATE A CATEGORY' : 'NEW TEMPLATE'}
                    icon={categories.length === 0 ? FolderPlus : Plus}
                    onClick={() => (categories.length === 0 ? setCategoryModal({ open: true, item: null }) : setTemplateModal({ open: true, item: null }))}
                  />
                </div>
              ) : visible.length === 0 ? (
                <div style={{ padding: '30px 18px', textAlign: 'center', fontSize: '0.76rem', color: C.muted }}>No templates match these filters.</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '760px' }}>
                    <thead>
                      <tr>
                        <th style={TH}>Template</th>
                        <th style={TH}>Category</th>
                        <th style={TH}>Channel</th>
                        <th style={TH}>Placeholders</th>
                        <th style={TH}>Files</th>
                        <th style={TH}>Active</th>
                        <th style={TH}>Updated</th>
                        <th style={{ ...TH, textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((t) => {
                        const ch = CHANNEL_META[t.channel];
                        const ChIcon = ch.icon;
                        const open = expanded === t.id;
                        return (
                          <React.Fragment key={t.id}>
                            <tr className="pt-row" style={{ opacity: t.is_active ? 1 : 0.55 }}>
                              <td style={TD}>
                                <button
                                  type="button"
                                  onClick={() => setExpanded(open ? null : t.id)}
                                  style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', maxWidth: '260px' }}
                                >
                                  {open ? <ChevronUp size={12} color={C.muted} /> : <ChevronDown size={12} color={C.muted} />}
                                  <span style={{ minWidth: 0 }}>
                                    <span style={{ display: 'block', fontWeight: 700, color: C.teal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
                                    {t.subject && <span style={{ display: 'block', fontSize: '0.66rem', color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject}</span>}
                                  </span>
                                </button>
                              </td>
                              <td style={TD}>
                                <span style={{ padding: '2px 8px', borderRadius: '20px', fontSize: '0.66rem', fontWeight: 700, background: C.limeDim, color: '#4a5e00', border: `1px solid ${C.limeBorder}`, whiteSpace: 'nowrap' }}>{t.category?.name ?? '—'}</span>
                              </td>
                              <td style={TD}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: ch.color, fontWeight: 700, fontSize: '0.72rem' }}>
                                  <ChIcon size={13} /> {ch.label}
                                </span>
                              </td>
                              <td style={TD} title={t.variables.map((v) => `{{${v}}}`).join('  ')}>
                                {t.variables.length === 0 ? <span style={{ color: C.muted }}>none</span> : <span style={{ fontWeight: 700 }}>{t.variables.length}</span>}
                              </td>
                              <td style={TD}>
                                {t.attachments.length === 0 ? (
                                  <span style={{ color: C.muted }}>—</span>
                                ) : (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: 700 }}>
                                    <Paperclip size={12} color={C.tealMid} /> {t.attachments.length}
                                  </span>
                                )}
                              </td>
                              <td style={TD}>
                                <MiniSwitch on={t.is_active} busy={busyIds.has(t.id)} onClick={() => handleToggleActive(t)} label={`${t.name} active`} />
                              </td>
                              <td style={{ ...TD, color: C.sub, whiteSpace: 'nowrap' }}>{fmtDate(t.updated_at)}</td>
                              <td style={{ ...TD, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                <span style={{ display: 'inline-flex', gap: '6px' }}>
                                  <button type="button" title="Edit template" onClick={() => setTemplateModal({ open: true, item: t })} style={iconBtn()}>
                                    <Pencil size={13} />
                                  </button>
                                  <button
                                    type="button"
                                    title="Delete template"
                                    onClick={() => {
                                      setDeleteError(null);
                                      setDeleteTarget({ kind: 'template', id: t.id, name: t.name });
                                    }}
                                    style={iconBtn(true)}
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                </span>
                              </td>
                            </tr>

                            {open && (
                              <tr>
                                <td colSpan={8} style={{ padding: '0 12px 12px', background: '#fbfcfc', borderBottom: `1px solid ${G.softer}` }}>
                                  <div style={{ padding: '11px 13px', background: C.white, border: `1px solid ${G.line}`, borderLeft: `3px solid ${C.lime}`, borderRadius: '8.3px' }}>
                                    {t.subject && <div style={{ fontSize: '0.74rem', fontWeight: 700, color: C.teal, marginBottom: '6px' }}>{t.subject}</div>}
                                    <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem', color: '#1f2937', lineHeight: 1.6 }}>{t.body}</div>
                                    {t.attachments.length > 0 && (
                                      <div style={{ marginTop: '9px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                        {t.attachments.map((a) => (
                                          <a key={a.path} href={a.url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 9px', borderRadius: '20px', background: G.shellBg, border: `1px solid ${G.line}`, color: C.teal, fontSize: '0.66rem', fontWeight: 700, textDecoration: 'none' }}>
                                            <Paperclip size={11} /> {a.file_name} <span style={{ color: C.muted, fontWeight: 600 }}>{fmtSize(a.size)}</span>
                                          </a>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!loading && !error && categories.length > 0 && templates.length > 0 && (
        <div style={{ marginTop: '11px' }}>
          <Notice tone="info">Staff only ever see active templates when they send a proposal. Hiding a template never affects proposals that were already sent.</Notice>
        </div>
      )}
    </div>
  );
};

export default ProposalTemplates;